import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuthStore } from '@dukene/shared';
import { CheckCircle2, XCircle, Loader2, ArrowRight } from 'lucide-react';

export default function VerifyEmail() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const verifyEmail = useAuthStore((state) => state.verifyEmail);

    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (!token) {
            setStatus('error');
            setErrorMessage('Verification token is missing.');
            return;
        }

        const verify = async () => {
            try {
                await verifyEmail(token);
                setStatus('success');
            } catch (err: any) {
                setStatus('error');
                setErrorMessage(err.message || 'Verification failed. The link may be expired or invalid.');
            }
        };

        verify();
    }, [token, verifyEmail]);

    return (
        <div className="min-h-screen flex bg-white">
            {/* Left Side - Branding (Same as Login) */}
            <div className="hidden lg:flex lg:w-1/2 bg-white border-r border-gray-100 relative">
                <div className="flex flex-col justify-between w-full p-16 xl:p-24">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center">
              <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain p-0.5" />
            </div>
                        <div>
                            <h1 className="text-xl font-bold text-black tracking-tight">Dukene</h1>
                            <p className="text-xs text-gray-500 font-medium tracking-wide">MERCHANTS</p>
                        </div>
                    </div>

                    <div className="space-y-16">
                        <div>
                            <h2 className="text-6xl font-light text-black mb-6 leading-tight tracking-tight">
                                Secure Your
                                <br />
                                Account
                            </h2>
                            <div className="w-16 h-0.5 bg-black mb-6"></div>
                            <p className="text-lg text-gray-600 leading-relaxed font-light">
                                Verifying your email ensures your account is secure and you receive important updates about your store.
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-8 text-xs text-gray-400 font-light">
                        <span>© 2024 Dukene</span>
                        <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                        <span>All rights reserved</span>
                    </div>
                </div>
            </div>

            {/* Right Side - Status */}
            <div className="flex-1 flex items-center justify-center bg-white px-8 lg:px-20 xl:px-32 py-12">
                <div className="w-full max-w-md text-center">
                    {/* Mobile Logo */}
                    <div className="lg:hidden flex items-center justify-center gap-3 mb-16">
                        <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center">
              <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain p-0.5" />
            </div>
                        <div>
                            <h1 className="text-xl font-bold text-black">Dukene</h1>
                            <p className="text-xs text-gray-500 font-medium">MERCHANTS</p>
                        </div>
                    </div>

                    {status === 'loading' && (
                        <div className="space-y-6">
                            <div className="flex justify-center">
                                <Loader2 className="w-16 h-16 text-black animate-spin" />
                            </div>
                            <h2 className="text-3xl font-light text-black tracking-tight">Verifying your email...</h2>
                            <p className="text-gray-500 font-light">Please wait while we confirm your account.</p>
                        </div>
                    )}

                    {status === 'success' && (
                        <div className="space-y-6">
                            <div className="flex justify-center">
                                <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center">
                                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                                </div>
                            </div>
                            <h2 className="text-4xl font-light text-black tracking-tight">Verification Successful!</h2>

                            {window.innerWidth < 1024 ? (
                                <>
                                    <p className="text-gray-500 text-lg font-light leading-relaxed">
                                        Your email has been verified. Since you're on a mobile device, please head back to your computer to manage your store with the full dashboard experience.
                                    </p>
                                    <div className="pt-8 space-y-4">
                                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                                            <p className="text-sm font-medium text-gray-900 mb-1">Get the Merchant App</p>
                                            <p className="text-xs text-gray-500 mb-4">Manage your store on the go with our dedicated mobile app.</p>
                                            <a
                                                href="https://play.google.com/store/apps/details?id=com.dukene.merchant"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 px-6 h-10 bg-black text-white rounded-lg font-medium text-sm transition-transform active:scale-95"
                                            >
                                                Download for Android
                                            </a>
                                        </div>
                                        <div className="text-center">
                                            <p className="text-xs text-gray-400">Or continue to</p>
                                            <Link to="/login" className="text-sm text-emerald font-medium hover:underline">
                                                Sign In on this device
                                            </Link>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="text-gray-500 text-lg font-light leading-relaxed">
                                        Your email has been verified. You can now access your merchant dashboard and start selling.
                                    </p>
                                    <div className="pt-8">
                                        <Link
                                            to="/login"
                                            className="w-full h-12 bg-black hover:bg-gray-900 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2 group text-sm"
                                        >
                                            Sign In to Your Dashboard
                                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                        </Link>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="space-y-6">
                            <div className="flex justify-center">
                                <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center">
                                    <XCircle className="w-10 h-10 text-red-600" />
                                </div>
                            </div>
                            <h2 className="text-4xl font-light text-black tracking-tight">Verification Failed</h2>
                            <p className="text-red-700 font-light mb-8">
                                {errorMessage}
                            </p>
                            <div className="pt-8 space-y-4">
                                <Link
                                    to="/register"
                                    className="w-full h-12 bg-black hover:bg-gray-900 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2 text-sm"
                                >
                                    Return to Registration
                                </Link>
                                <div className="text-center">
                                    <Link to="/login" className="text-sm text-gray-500 hover:text-black font-light transition-colors">
                                        Back to Login
                                    </Link>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
